import React from 'react';
import { Link } from 'react-router-dom';
import SharedHeaderLayout from '../components/layout/SharedHeaderLayout.jsx';
import background from '../assets/background.webp';
import logo from '../assets/logo.webp';

const AboutPage = () => {
    return (
        <div className="min-h-screen bg-gaia-bg flex flex-col">
            <SharedHeaderLayout />
            <div
                className="flex-grow flex flex-col items-center justify-center relative overflow-hidden"
                style={{
                    backgroundImage: `url(${background})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            >
                {/* Dark overlay for readability */}
                <div className="absolute inset-0 bg-black/20"></div>

                {/* Main Content */}
                <main className="relative z-10 flex flex-col items-center justify-center px-4 py-8 max-w-3xl w-full">
                    {/* Logo and Text Section */}
                    <div className="text-center mb-12 space-y-6" style={{ marginTop: '-10px' }}>
                        {/* Logo */}
                        <div className="flex justify-center mb-4">
                            <img
                                src={logo}
                                alt="Fable Table"
                                className="w-44 h-44 md:w-56 md:h-56 object-contain drop-shadow-2xl"
                            />
                        </div>

                        {/* Title */}
                        <h1
                            className="text-3xl md:text-4xl text-gray-100 drop-shadow-lg tracking-wide font-bold mb-6"
                            style={{
                                fontFamily: '"Cinzel", "Times New Roman", serif',
                                textShadow: '0 0 10px rgba(0,0,0,0.9), 2px 2px 4px rgba(0,0,0,0.8)'
                            }}
                        >
                            About
                        </h1>

                        {/* Description in semi-transparent box */}
                        <div className="bg-black/40 backdrop-blur-sm border border-gray-700/50 rounded-lg p-8 shadow-2xl" style={{ width: 'calc(100% + 20px)', maxWidth: 'calc(48rem + 20px)' }}>
                            <p
                                className="text-lg md:text-xl text-gray-200 leading-relaxed text-left mb-4"
                                style={{
                                    fontFamily: '"Times New Roman", Georgia, serif',
                                    textShadow: '0 0 10px rgba(0,0,0,0.9), 1px 1px 2px rgba(0,0,0,0.8)'
                                }}
                            >
                                Fable Table is a real-time AI orchestration platform for interactive storytelling.
                                It coordinates multiple generative AI models — including LLM-driven narration,
                                speech synthesis, image generation, and dynamic audio — across concurrent
                                multiplayer sessions with low-latency synchronization.
                            </p>
                            <p
                                className="text-lg md:text-xl text-gray-200 leading-relaxed text-left mb-4"
                                style={{
                                    fontFamily: '"Times New Roman", Georgia, serif',
                                    textShadow: '0 0 10px rgba(0,0,0,0.9), 1px 1px 2px rgba(0,0,0,0.8)'
                                }}
                            >
                                The platform serves creative professionals and communities running collaborative
                                narrative experiences, powering the next generation of online role-playing adventures.
                            </p>
                            <p
                                className="text-lg md:text-xl text-gray-200 leading-relaxed text-left italic"
                                style={{
                                    fontFamily: '"Times New Roman", Georgia, serif',
                                    textShadow: '0 0 10px rgba(0,0,0,0.9), 1px 1px 2px rgba(0,0,0,0.8)'
                                }}
                            >
                                Come journey with us.
                            </p>
                        </div>

                        {/* Discord Link */}
                        <a
                            href="https://discord.gg/WCZwgJbSqZ"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-gray-300 hover:text-amber-400 transition-colors duration-200 mt-6"
                            title="Join our Discord"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-6 h-6"
                            >
                                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z" />
                            </svg>
                            <span
                                className="text-sm"
                                style={{
                                    fontFamily: '"Cinzel", "Times New Roman", serif'
                                }}
                            >
                                Join our Discord
                            </span>
                        </a>
                        {/* Footer Links */}
                        <div className="mt-6">
                            <Link
                                to="/privacy"
                                className="text-gray-400 hover:text-amber-400 transition-colors duration-200 text-sm"
                                style={{ fontFamily: '"Cinzel", "Times New Roman", serif' }}
                            >
                                Privacy Policy
                            </Link>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AboutPage;
